import { useMemo, useState } from "react";
import { FileImage, FolderKanban, Music2, RotateCcw, Trash2, Video as VideoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeleteDialog } from "@/components/delete-dialog";
import {
  useDeleteVideoPermanent,
  useRestoreVideo,
  useTrashList,
} from "@/hooks/use-videos";
import { useDeleteProjectPermanent, useRestoreProject } from "@/hooks/use-projects";
import { useWindowedList } from "@/hooks/use-windowed-list";
import { canDeleteProject } from "@/lib/api-types";
import { useAuth } from "@/hooks/use-auth";
import type { TrashItem, TrashProject, TrashVideo } from "@/lib/api-types";
import { cn } from "@/lib/utils";

type Props = {
  /** For consistency with other pages, though trash page has no children/nested routes. */
  basePath?: string;
};

function formatDate(value: string | undefined | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function AssetIcon({ assetType, className }: { assetType?: string; className?: string }) {
  if (assetType === "audio") return <Music2 className={className} strokeWidth={1.5} />;
  if (assetType === "image") return <FileImage className={className} strokeWidth={1.5} />;
  return <VideoIcon className={className} strokeWidth={1.5} />;
}

function daysLeftClass(daysLeft: number | null): string {
  if (daysLeft == null) return "text-muted-foreground";
  if (daysLeft <= 2) return "text-destructive";
  if (daysLeft <= 5) return "text-amber-500 dark:text-amber-400";
  return "text-muted-foreground";
}

/**
 * Trash page — unified list of trashed videos + projects.
 * Model locked 2026-07-08 — see memory: project-trash-delete-model.
 *   - Videos: EDIT sees own trashed, FULL_ACCESS+ sees all.
 *   - Projects: ADMIN + FULL_ACCESS only (matches DELETE_PROJECT perm).
 *   - Day-8 notification links here.
 */
export function TrashPage(_props: Props) {
  const { role } = useAuth();
  const canSeeProjects = canDeleteProject(role);
  const { data: items, isLoading, isError, refetch } = useTrashList();

  const restoreVideo = useRestoreVideo();
  const restoreProject = useRestoreProject();
  const permanentVideo = useDeleteVideoPermanent(undefined);
  const permanentProject = useDeleteProjectPermanent();

  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
  const [permanentTarget, setPermanentTarget] = useState<TrashItem | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const { projects, videos } = useMemo(() => {
    const p: TrashProject[] = [];
    const v: TrashVideo[] = [];
    for (const item of items || []) {
      if (item.kind === "project") p.push(item);
      else v.push(item);
    }
    return { projects: p, videos: v };
  }, [items]);

  // Window the assets list (projects in trash are few); append on scroll.
  const { visible: pagedVideos, hasMore, sentinelRef } = useWindowedList(videos);

  async function handleRestore(item: TrashItem) {
    setPendingRestoreId(item.id);
    setRestoreError(null);
    try {
      if (item.kind === "video") {
        await restoreVideo.mutateAsync(item.id);
      } else {
        await restoreProject.mutateAsync(item.id);
      }
    } catch (e) {
      // Surface the server's message (e.g. the plan-cap 403 "You've reached your
      // plan's project limit (2)…") instead of failing silently.
      setRestoreError(
        e instanceof Error && e.message ? e.message : "Couldn't restore this item. Please try again."
      );
    } finally {
      setPendingRestoreId(null);
    }
  }

  async function handlePermanent(item: TrashItem) {
    if (item.kind === "video") {
      await permanentVideo.mutateAsync(item.id);
    } else {
      await permanentProject.mutateAsync(item.id);
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">Trash</h1>
        <p className="text-sm text-muted-foreground">
          Items in trash are recoverable for 10 days, then permanently deleted. Trash still counts toward your storage quota.
        </p>
      </header>

      {restoreError && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <span>{restoreError}</span>
          <button
            type="button"
            onClick={() => setRestoreError(null)}
            className="shrink-0 font-medium underline"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {isLoading && (
        <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          Loading trash…
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-4 text-sm text-destructive">
          Couldn't load trash.{" "}
          <button type="button" onClick={() => refetch()} className="underline">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (items?.length ?? 0) === 0 && (
        <div className="grid place-items-center rounded-xl border border-dashed border-border bg-card px-4 py-16 text-center">
          <Trash2 className="mb-3 h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
          <div className="font-medium text-foreground">Trash is empty</div>
          <p className="mt-1 text-sm text-muted-foreground">
            Deleted items land here for 10 days before they're permanently removed.
          </p>
        </div>
      )}

      {!isLoading && !isError && (items?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-6">
          {canSeeProjects && projects.length > 0 && (
            <TrashSection
              title="Projects"
              subtitle={`${projects.length} ${projects.length === 1 ? "project" : "projects"}`}
            >
              {projects.map((p) => (
                <ProjectRow
                  key={p.id}
                  item={p}
                  restoring={pendingRestoreId === p.id}
                  onRestore={() => handleRestore(p)}
                  onPermanent={() => setPermanentTarget(p)}
                />
              ))}
            </TrashSection>
          )}

          {videos.length > 0 && (
            <TrashSection
              title="Assets"
              subtitle={`${videos.length} ${videos.length === 1 ? "asset" : "assets"}`}
            >
              {pagedVideos.map((v) => (
                <VideoRow
                  key={v.id}
                  item={v}
                  restoring={pendingRestoreId === v.id}
                  onRestore={() => handleRestore(v)}
                  onPermanent={() => setPermanentTarget(v)}
                />
              ))}
              {hasMore && <div ref={sentinelRef} aria-hidden className="h-1" />}
            </TrashSection>
          )}
        </div>
      )}

      {permanentTarget && (
        <DeleteDialog
          open={!!permanentTarget}
          onOpenChange={(next) => {
            if (!next) setPermanentTarget(null);
          }}
          mode="permanent"
          itemLabel={permanentTarget.kind === "project" ? "project" : "asset"}
          itemName={
            permanentTarget.kind === "project" ? permanentTarget.name : permanentTarget.title
          }
          onPermanent={async () => {
            const target = permanentTarget;
            if (!target) return;
            await handlePermanent(target);
            setPermanentTarget(null);
          }}
        />
      )}
    </div>
  );
}

function TrashSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
        {subtitle && <span className="text-xs text-muted-foreground">· {subtitle}</span>}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function ProjectRow({
  item,
  restoring,
  onRestore,
  onPermanent,
}: {
  item: TrashProject;
  restoring: boolean;
  onRestore: () => void;
  onPermanent: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-secondary/60">
        <FolderKanban className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{item.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          Project · Trashed {formatDate(item.deletedAt)}
        </div>
      </div>
      <span className={cn("shrink-0 text-xs font-medium", daysLeftClass(item.daysLeft))}>
        {item.daysLeft != null ? `${item.daysLeft}d left` : "—"}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRestore}
        disabled={restoring}
        aria-label="Restore"
      >
        <RotateCcw />
        <span className="hidden sm:inline">{restoring ? "Restoring…" : "Restore"}</span>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onPermanent}
        aria-label="Delete"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 />
        <span className="hidden sm:inline">Delete</span>
      </Button>
    </div>
  );
}

function VideoRow({
  item,
  restoring,
  onRestore,
  onPermanent,
}: {
  item: TrashVideo;
  restoring: boolean;
  onRestore: () => void;
  onPermanent: () => void;
}) {
  const isCascadeChild = item.deletionType === "cascade_from_project";

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
      <div className="grid h-10 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
        {item.assetType === "video" && item.cdnUrl ? (
          <video src={item.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : item.assetType === "image" && item.cdnUrl ? (
          <img src={item.cdnUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <AssetIcon assetType={item.assetType} className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{item.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {item.projectName || "Unknown project"} · {item.uploaderName || "Unknown"} · Trashed{" "}
          {formatDate(item.deletedAt)}
        </div>
        {isCascadeChild && (
          <div className="mt-0.5 text-[10px] italic text-muted-foreground">
            Trashed with its project — restore project to restore this asset
          </div>
        )}
      </div>
      <span className={cn("shrink-0 text-xs font-medium", daysLeftClass(item.daysLeft))}>
        {item.daysLeft != null ? `${item.daysLeft}d left` : "—"}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRestore}
        disabled={restoring || isCascadeChild}
        title={isCascadeChild ? "Restore the parent project to restore this asset" : undefined}
        aria-label="Restore"
      >
        <RotateCcw />
        <span className="hidden sm:inline">{restoring ? "Restoring…" : "Restore"}</span>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onPermanent}
        aria-label="Delete"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 />
        <span className="hidden sm:inline">Delete</span>
      </Button>
    </div>
  );
}
